import { useCallback } from 'react';
import { useMediaQuery } from '@mui/material';
import { formatCents } from '@src/common';
import { useAppSelector, useWidgetParam, useTranslate, useWidgetsTheme } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import { adderResumeSelector } from '@src/stores/product';
import useBreakpoint from '@src/hooks/useBreakpoint';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import { useIsTargetVisible } from '@src/common/hooks/useIsTargetVisible';
import type { UnitPrice } from '@src/domains/products/features/product-booking/core/computeUnitPrice';
import { getDisplayCents } from '@src/domains/products/features/product-booking/core/getDisplayCents';

const NARROW_VIEWPORT_QUERY = '(max-width: 360px)';

type Args = {
  cardRef: React.RefObject<HTMLElement | null>;
  unitPrice: UnitPrice | null;
};

type ViewModel = {
  shouldRender: boolean;
  visible: boolean;
  stacked: boolean;
  showTotal: boolean;
  priceLabel: string;
  priceValue: string;
  ctaLabel: string;
  actionColor: string;
  handleViewSummary: () => void;
};

export function useProductBookingSummary({ cardRef, unitPrice }: Args): ViewModel {
  const widgetId = useWidgetId();
  const { i18n } = useTranslate();
  const customCtaText = useWidgetParam('add-to-cart-cta-text');
  const actionColor = useWidgetsTheme('actionColor');
  const { isMobile } = useBreakpoint();
  const isNarrow = useMediaQuery(NARROW_VIEWPORT_QUERY);
  const { isVisible: isProductBookingCardVisible } = useIsTargetVisible(cardRef);
  // Reservation total mirrors the in-card ReservationTotal, which is locale/currency aware.
  // The unit price keeps `@src/common`'s formatter to stay in sync with PriceContainer.
  const { formatCents: formatTotalCents } = useCurrencyFormat();

  const { totalCents } = useAppSelector(adderResumeSelector(widgetId));

  const ctaLabel = customCtaText || i18n('product_booking_add_to_cart');

  const handleViewSummary = useCallback(() => {
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [cardRef]);

  const showTotal = totalCents > 0;
  const stacked = showTotal && isNarrow;

  const unitCents = unitPrice ? getDisplayCents(unitPrice) : null;
  const shouldRender = isMobile && (showTotal || unitCents !== null);

  const hasOldPrice = unitPrice?.kind === 'single' && unitPrice.oldCents != null && unitPrice.oldCents > 0;
  const isStartingFrom = unitPrice?.kind === 'range' || !hasOldPrice;

  const priceLabel = showTotal
    ? i18n('product_booking_total_label')
    : isStartingFrom
    ? i18n('product_booking_price_starting_from')
    : i18n('product_booking_price_at_sight_label');
  const priceValue = showTotal ? formatTotalCents(totalCents) : unitCents !== null ? formatCents(unitCents) : '';

  return {
    shouldRender,
    // Only show the summary if the card is not visible, otherwise the summary is already visible in the card
    visible: !isProductBookingCardVisible,
    stacked,
    showTotal,
    priceLabel,
    priceValue,
    ctaLabel,
    actionColor,
    handleViewSummary,
  };
}
